import { useEffect, useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import { useDebounceCallback } from './useDebounce';

interface UseLayoutDataOptions {
  data: unknown[];
  itemsCountPerPage: number;
  hasNextFetch?: boolean;
  handleNextFetch?: () => Promise<void> | void;
  withPreloadData?: boolean;
  isDataStatic?: boolean;
}

export function useLayoutData({
  data,
  itemsCountPerPage,
  handleNextFetch,
  hasNextFetch = false,
  withPreloadData = false,
  isDataStatic = false
}: UseLayoutDataOptions) {
  const [slicedData, setSlicedData] = useState<unknown[]>([]);
  const [count, setCount] = useState<number>(itemsCountPerPage);
  const [hasNext, setHasNext] = useState(false);
  const dataCount = data.length;

  useEffect(() => {
    setCount(itemsCountPerPage);
  }, [itemsCountPerPage]);

  useAsyncEffect(
    async (isMounted: () => boolean) => {
      if (dataCount < count && hasNextFetch && handleNextFetch) {
        if (withPreloadData) {
          const preloadData = Array.from(
            { length: itemsCountPerPage || 3 },
            () => null
          );
          setSlicedData((prevState) => [...prevState, ...preloadData]);
        }

        await handleNextFetch();
        return;
      }

      if (!isMounted()) return;

      setSlicedData([...data].slice(0, count));
    },
    [data, count, hasNextFetch, handleNextFetch]
  );

  const updateHasNextState = useDebounceCallback(() => {
    const hasNext =
      slicedData.filter(Boolean).length < dataCount || hasNextFetch;
    setHasNext(hasNext);
  });

  useEffect(updateHasNextState, [slicedData, dataCount, hasNextFetch]);

  const loadNext = () => {
    setCount((prevState) => prevState + itemsCountPerPage);
  };

  const reset = () => {
    setCount(itemsCountPerPage);
  };

  return {
    hasNext,
    loadNext,
    data: isDataStatic ? data : slicedData,
    reset
  };
}
